import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Camera, Save, X, ArrowLeft, ExternalLink, Link } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useProduct, useCreateProduct, useUpdateProduct } from '../hooks/useQueries';
import { Product } from '../types';

export function ProductFormPage() {
  const navigate = useNavigate();
  const { productId } = useParams();
  const { user } = useAuth();
  const isEditing = !!productId;
  
  const { data: product, isLoading: productLoading } = useProduct(productId || '');
  const createProductMutation = useCreateProduct();
  const updateProductMutation = useUpdateProduct();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    image: '',
    category: '',
    inStock: true,
    isAffiliate: false,
    affiliateUrl: '',
    affiliateProvider: 'shopee' as 'shopee' | 'tokopedia' | 'bukalapak' | 'blibli' | 'custom'
  });

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name,
        description: product.description,
        price: product.price.toString(),
        image: product.image,
        category: product.category || '',
        inStock: product.inStock,
        isAffiliate: product.isAffiliate,
        affiliateUrl: product.affiliateUrl || '',
        affiliateProvider: product.affiliateProvider || 'shopee'
      });
    }
  }, [product]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    // Validate affiliate URL if affiliate is enabled
    if (formData.isAffiliate && !formData.affiliateUrl.trim()) {
      alert('Please provide an affiliate URL');
      return;
    }

    try {
      const productData = {
        userId: user.id,
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        image: formData.image || 'https://images.pexels.com/photos/1454171/pexels-photo-1454171.jpeg?auto=compress&cs=tinysrgb&w=400',
        category: formData.category,
        inStock: formData.inStock,
        isAffiliate: formData.isAffiliate,
        affiliateUrl: formData.isAffiliate ? formData.affiliateUrl : undefined,
        affiliateProvider: formData.isAffiliate ? formData.affiliateProvider : undefined,
      };

      if (isEditing && productId) {
        await updateProductMutation.mutateAsync({ productId, updates: productData });
      } else {
        await createProductMutation.mutateAsync(productData);
      }

      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Failed to save product. Please try again.');
    }
  };

  const sampleImages = [
    'https://images.pexels.com/photos/1457842/pexels-photo-1457842.jpeg?auto=compress&cs=tinysrgb&w=400',
    'https://images.pexels.com/photos/1454171/pexels-photo-1454171.jpeg?auto=compress&cs=tinysrgb&w=400',
    'https://images.pexels.com/photos/1454166/pexels-photo-1454166.jpeg?auto=compress&cs=tinysrgb&w=400',
    'https://images.pexels.com/photos/1458683/pexels-photo-1458683.jpeg?auto=compress&cs=tinysrgb&w=400',
    'https://images.pexels.com/photos/1927259/pexels-photo-1927259.jpeg?auto=compress&cs=tinysrgb&w=400',
    'https://images.pexels.com/photos/1464625/pexels-photo-1464625.jpeg?auto=compress&cs=tinysrgb&w=400'
  ];

  const isLoading = createProductMutation.isPending || updateProductMutation.isPending;

  if (productLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading product...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </button>
            <h1 className="text-xl font-semibold text-gray-900">
              {isEditing ? 'Edit Product' : 'Add New Product'}
            </h1>
            <div className="w-24" />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Product Type Toggle */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Type</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
                !formData.isAffiliate 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="productType"
                  checked={!formData.isAffiliate}
                  onChange={() => setFormData(prev => ({ ...prev, isAffiliate: false, affiliateUrl: '', affiliateProvider: 'shopee' }))}
                  className="sr-only"
                />
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Save className="w-6 h-6 text-blue-600" />
                  </div>
                  <h4 className="font-semibold text-gray-900">Direct Sale</h4>
                  <p className="text-sm text-gray-600">Sell directly with cart functionality</p>
                </div>
              </label>

              <label className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all ${
                formData.isAffiliate 
                  ? 'border-purple-500 bg-purple-50' 
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="productType"
                  checked={formData.isAffiliate}
                  onChange={() => setFormData(prev => ({ ...prev, isAffiliate: true }))}
                  className="sr-only"
                />
                <div className="text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <ExternalLink className="w-6 h-6 text-purple-600" />
                  </div>
                  <h4 className="font-semibold text-gray-900">Affiliate Product</h4>
                  <p className="text-sm text-gray-600">Link to external marketplace</p>
                </div>
              </label>
            </div>
          </div>

          {/* Affiliate Settings */}
          {formData.isAffiliate && (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Link className="w-5 h-5" />
                Affiliate Settings
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Affiliate Provider
                  </label>
                  <select
                    value={formData.affiliateProvider}
                    onChange={(e) => setFormData(prev => ({ ...prev, affiliateProvider: e.target.value as any }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="shopee">Shopee</option>
                    <option value="tokopedia">Tokopedia</option>
                    <option value="bukalapak">Bukalapak</option>
                    <option value="blibli">Blibli</option>
                    <option value="custom">Other/Custom</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Affiliate URL *
                  </label>
                  <input
                    type="url"
                    required={formData.isAffiliate}
                    value={formData.affiliateUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, affiliateUrl: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="https://shopee.co.id/product-link"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Visitors will be redirected to this URL when they click the product
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Product Image */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Product Image
            </label>
            
            {formData.image ? (
              <div className="relative">
                <img
                  src={formData.image}
                  alt="Product"
                  className="w-full h-64 object-cover rounded-xl border border-gray-200"
                />
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                  className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full shadow-lg hover:bg-red-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
                <Camera className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">Choose a sample image or add your own URL</p>
                
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {sampleImages.map((imageUrl, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, image: imageUrl }))}
                      className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-blue-500 transition-colors"
                    >
                      <img
                        src={imageUrl}
                        alt={`Sample ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
                
                <div className="text-sm text-gray-500">
                  Or paste an image URL:
                </div>
                <input
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  className="mt-2 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onBlur={(e) => {
                    if (e.target.value) {
                      setFormData(prev => ({ ...prev, image: e.target.value }));
                      e.target.value = '';
                    }
                  }}
                />
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Product Details</h3>
            
            {/* Product Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter product name"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                required
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Describe your product..."
              />
            </div>

            {/* Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price (IDR) *
              </label>
              <input
                type="number"
                required
                min="0"
                step="1000"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0"
              />
              {formData.isAffiliate && (
                <p className="text-sm text-gray-500 mt-1">
                  Display price for reference (visitors will see actual price on the affiliate site)
                </p>
              )}
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select category</option>
                <option value="jewelry">Jewelry</option>
                <option value="clothing">Clothing</option>
                <option value="accessories">Accessories</option>
                <option value="electronics">Electronics</option>
                <option value="home">Home & Living</option>
                <option value="beauty">Beauty</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Stock Status */}
            <div>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.inStock}
                  onChange={(e) => setFormData(prev => ({ ...prev, inStock: e.target.checked }))}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Product is {formData.isAffiliate ? 'available' : 'in stock'}
                </span>
              </label>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-6">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="flex-1 border border-gray-200 text-gray-700 py-3 px-4 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:transform-none flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {isEditing ? 'Update Product' : 'Add Product'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
